import React, { useState, useCallback } from 'react';
import { soundManager } from './entities/SoundManager';

const AudioControls: React.FC = () => {
  const [volume, setVolume] = useState(soundManager.masterVolume);
  const [muted, setMuted]   = useState(soundManager.muted);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    soundManager.setMasterVolume(vol);
    if (vol > 0 && muted) {
      setMuted(false);
      soundManager.setMuted(false);
    }
  }, [muted]);

  const handleMuteToggle = useCallback(() => {
    const turnOn = soundManager.muted || soundManager.masterVolume === 0;
    if (turnOn && (soundManager.masterVolume === 0 || window.matchMedia('(max-width: 760px)').matches)) {
      soundManager.setMasterVolume(1);
      setVolume(1);
    }
    soundManager.setMuted(!turnOn);
    setMuted(!turnOn);
    if (turnOn) soundManager.resumeAudio(true);
  }, []);

  const effectiveVolume = muted ? 0 : volume;
  const silent = effectiveVolume === 0;

  return (
    <div
      className="audio-control flex items-center gap-2 select-none"
      data-audio="off"
      style={{
        background: 'var(--s2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        padding: '5px 10px',
      }}
    >
      <button
        className="audio-button"
        onClick={handleMuteToggle}
        aria-label={silent ? 'Unmute audio' : 'Mute audio'}
        title={silent ? 'Unmute' : 'Mute'}
        style={{ color: silent ? 'var(--text-muted)' : 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center', transition: 'color 0.15s' }}
      >
        <VolumeIcon level={effectiveVolume} />
      </button>

      <input
        type="range"
        min="0" max="1" step="0.05"
        value={effectiveVolume}
        onChange={handleVolumeChange}
        aria-label="Master volume"
        style={{ width: 64, cursor: 'pointer', accentColor: 'var(--gold)', height: 3 }}
      />

      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: 'var(--text-muted)', minWidth: 28, textAlign: 'right' }}>
        {Math.round(effectiveVolume * 100)}%
      </span>
    </div>
  );
};

const VolumeIcon: React.FC<{ level: number }> = ({ level }) => {
  const s = 16;
  if (level === 0) return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.63 3.63a1 1 0 000 1.41L7.29 8.7 7 9H4a1 1 0 00-1 1v4a1 1 0 001 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18a7.9 7.9 0 01-1.6.91c-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91a9.9 9.9 0 002.22-1.31l1.34 1.34a1 1 0 101.41-1.41L5.05 3.63a1 1 0 00-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53A9.8 9.8 0 0021 12c0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7z"/>
    </svg>
  );
  if (level < 0.5) return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.5 12A4.5 4.5 0 0016 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
    </svg>
  );
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>
  );
};

export default AudioControls;
